import { useRef, useState, useEffect, useCallback } from 'react';
import { categories } from '@/mocks/categories';
import CategoryCard from '@/components/base/CategoryCard';

const categoryImages = [
  'https://readdy.ai/api/search-image?query=Designer%20leather%20sneakers%20and%20loafers%20artistically%20arranged%20on%20white%20marble%20surface%2C%20top%20down%20view%2C%20natural%20sunlight%20casting%20soft%20shadows%2C%20luxury%20product%20photography%2C%20cream%20and%20beige%20color%20palette&width=560&height=416&seq=cat-v2-shoes&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Oversized%20cotton%20hoodie%20draped%20over%20wooden%20chair%20in%20sunlit%20room%2C%20cozy%20lifestyle%20photography%2C%20warm%20morning%20light%20through%20window%2C%20textile%20texture%20visible%2C%20neutral%20oatmeal%20and%20cream%20tones&width=560&height=416&seq=cat-v2-hoodies&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Premium%20cotton%20t-shirts%20in%20pastel%20colors%20neatly%20stacked%20on%20bamboo%20shelf%2C%20minimalist%20retail%20display%2C%20soft%20diffused%20lighting%2C%20clean%20composition%20with%20sage%20green%20and%20blush%20pink%20accents&width=560&height=416&seq=cat-v2-tshirts&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Classic%20denim%20jacket%20and%20wool%20blazer%20displayed%20on%20minimal%20clothing%20rack%20against%20white%20brick%20wall%2C%20industrial%20chic%20aesthetic%2C%20natural%20daylight%2C%20editorial%20fashion%20photography&width=560&height=416&seq=cat-v2-jackets&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Tailored%20trousers%20and%20linen%20shorts%20folded%20on%20light%20oak%20surface%2C%20flat%20lay%20photography%2C%20morning%20sunlight%2C%20earth%20tone%20colors%2C%20minimal%20styling%20with%20dried%20flowers&width=560&height=416&seq=cat-v2-pants&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Vintage%20baseball%20caps%20and%20wool%20beanies%20arranged%20on%20wooden%20hat%20stand%2C%20cozy%20cafe%20corner%20background%2C%20warm%20ambient%20lighting%2C%20retro%20americana%20aesthetic&width=560&height=416&seq=cat-v2-headwear&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Coordinated%20matching%20outfit%20sets%20on%20mannequin%20forms%2C%20minimal%20studio%20setup%2C%20soft%20studio%20lighting%2C%20monochrome%20beige%20and%20cream%20collection%2C%20contemporary%20fashion%20photography&width=560&height=416&seq=cat-v2-sets&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Organic%20cotton%20underwear%20folded%20in%20minimal%20drawer%20organizer%2C%20soft%20morning%20light%2C%20clean%20bedroom%20aesthetic%2C%20neutral%20linen%20texture%20background%2C%20lifestyle%20photography&width=560&height=416&seq=cat-v2-underwear&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Professional%20sports%20jersey%20hanging%20in%20locker%20room%20with%20dramatic%20side%20lighting%2C%20athletic%20aesthetic%2C%20dark%20background%20with%20warm%20spotlight%2C%20sport%20photography%20style&width=560&height=416&seq=cat-v2-jersey&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Silk%20scarves%20and%20minimalist%20jewelry%20displayed%20on%20velvet%20surface%2C%20jewelry%20boutique%20aesthetic%2C%20warm%20spotlight%20illumination%2C%20elegant%20product%20photography%20with%20rose%20gold%20reflections&width=560&height=416&seq=cat-v2-accessories&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Leather%20tote%20bag%20and%20crossbody%20purse%20placed%20on%20vintage%20wooden%20table%2C%20coffee%20shop%20setting%2C%20natural%20window%20light%2C%20warm%20caramel%20and%20tan%20leather%20tones&width=560&height=416&seq=cat-v2-bags&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Handcrafted%20leather%20belts%20with%20brass%20buckles%20coiled%20on%20raw%20linen%20fabric%2C%20artisan%20workshop%20background%2C%20warm%20natural%20light%2C%20rustic%20elegant%20product%20photography&width=560&height=416&seq=cat-v2-belts&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Designer%20fragrance%20bottles%20grouped%20on%20travertine%20stone%20surface%2C%20luxury%20boutique%20aesthetic%2C%20soft%20side%20lighting%2C%20glass%20reflections%2C%20amber%20and%20crystal%20tones&width=560&height=416&seq=cat-v2-perfumes&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Curated%20lifestyle%20products%20including%20candles%20and%20notebooks%20on%20marble%20tray%2C%20minimal%20flat%20lay%2C%20morning%20sunlight%2C%20warm%20neutral%20aesthetic%2C%20thoughtful%20composition&width=560&height=416&seq=cat-v2-other&orientation=landscape',
];

export default function CategoriesSection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    return () => el.removeEventListener('scroll', checkScroll);
  }, [checkScroll]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = 320;
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold mb-3">
            / Catégories /
          </span>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900">
            Nos catégories mode
          </h2>
          <p className="mt-3 text-sm text-foreground-500 max-w-lg mx-auto">
            Explorez notre sélection complète de catégories sur kaspreadsheet.com
          </p>
        </div>

        <div className="relative">
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-50 border border-background-200 text-foreground-600 hover:text-primary-600 hover:border-primary-300 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
          )}

          <div
            ref={scrollRef}
            className="flex gap-4 md:gap-5 overflow-x-auto scrollbar-hide scroll-smooth pb-2"
          >
            {categories.map((cat, idx) => (
              <CategoryCard
                key={cat.name}
                name={cat.name}
                url={cat.url}
                icon={cat.icon}
                imageUrl={categoryImages[idx]}
                index={idx}
              />
            ))}
          </div>

          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-50 border border-background-200 text-foreground-600 hover:text-primary-600 hover:border-primary-300 transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}